import { ArrowDown, ArrowUp, Plus, Trash } from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';
import type { TaskStatusMeta } from '../../lib/task-list';
import { trpc } from '../../lib/trpc';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Input } from '../../ui/input';

interface EditStatusesDialogProps {
  isOpen: boolean;
  onClose: () => void;
  statuses: TaskStatusMeta[];
  onChanged?: () => void;
}

const DEFAULT_COLORS = ['#8F86A5', '#3B82F6', '#F59E0B', '#16A34A', '#DC2626', '#9333EA'];

/** CRUD dialog for a workspace's Kanban/status-tab columns (`taskStatuses`), shared between both portals' Tasks pages. */
export function EditStatusesDialog({
  isOpen,
  onClose,
  statuses,
  onChanged,
}: EditStatusesDialogProps) {
  const utils = trpc.useUtils();
  const [newLabel, setNewLabel] = React.useState('');
  const [fallbackPrompt, setFallbackPrompt] = React.useState<TaskStatusMeta | null>(null);
  const [fallbackKey, setFallbackKey] = React.useState('');

  const invalidate = () => {
    utils.taskStatuses.list.invalidate();
    onChanged?.();
  };

  const createMutation = trpc.taskStatuses.create.useMutation({
    onSuccess: () => {
      setNewLabel('');
      invalidate();
    },
    onError: (e) => toast.error(`Failed to add status: ${e.message}`),
  });

  const updateMutation = trpc.taskStatuses.update.useMutation({
    onSuccess: () => invalidate(),
    onError: (e) => toast.error(`Failed to update status: ${e.message}`),
  });

  const reorderMutation = trpc.taskStatuses.reorder.useMutation({
    onSuccess: () => invalidate(),
    onError: (e) => toast.error(`Failed to reorder statuses: ${e.message}`),
  });

  const deleteMutation = trpc.taskStatuses.delete.useMutation({
    onSuccess: () => {
      setFallbackPrompt(null);
      invalidate();
    },
    onError: (e) => toast.error(`Failed to delete status: ${e.message}`),
  });

  const sorted = [...statuses].sort((a, b) => a.sortOrder - b.sortOrder);

  const move = (index: number, direction: -1 | 1) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= sorted.length) return;
    const reordered = [...sorted];
    [reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]];
    reorderMutation.mutate({ orderedIds: reordered.map((s) => s.id) });
  };

  const requestDelete = (status: TaskStatusMeta) => {
    if (sorted.length <= 1) {
      toast.error('A workspace must have at least one status.');
      return;
    }
    setFallbackPrompt(status);
    setFallbackKey(sorted.find((s) => s.id !== status.id)?.key || '');
  };

  const confirmDelete = () => {
    if (!fallbackPrompt || !fallbackKey) return;
    deleteMutation.mutate({ id: fallbackPrompt.id, fallbackStatusKey: fallbackKey });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>Edit Statuses</DialogTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Add, rename, reorder, recolor, or delete the status columns for this workspace.
          </p>
        </DialogHeader>

        {fallbackPrompt ? (
          <div className="space-y-4">
            <p className="text-sm text-foreground">
              Delete <span className="font-semibold">{fallbackPrompt.label}</span>? Tasks on this
              status need to move somewhere — choose a fallback:
            </p>
            <select
              value={fallbackKey}
              onChange={(e) => setFallbackKey(e.target.value)}
              className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
            >
              {sorted
                .filter((s) => s.id !== fallbackPrompt.id)
                .map((s) => (
                  <option key={s.id} value={s.key}>
                    {s.label}
                  </option>
                ))}
            </select>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" size="sm" onClick={() => setFallbackPrompt(null)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={confirmDelete}
                disabled={deleteMutation.isPending}
              >
                Delete & Reassign
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-col gap-2">
              {sorted.map((status, index) => (
                <div key={status.id} className="flex items-center gap-2 rounded-lg border p-2">
                  <input
                    type="color"
                    value={status.color}
                    onChange={(e) =>
                      updateMutation.mutate({ id: status.id, color: e.target.value })
                    }
                    className="h-7 w-7 rounded border cursor-pointer shrink-0"
                  />
                  <Input
                    defaultValue={status.label}
                    onBlur={(e) => {
                      const label = e.target.value.trim();
                      if (label && label !== status.label) {
                        updateMutation.mutate({ id: status.id, label });
                      }
                    }}
                    className="h-8 text-sm flex-1"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    disabled={index === 0}
                    onClick={() => move(index, -1)}
                  >
                    <ArrowUp className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    disabled={index === sorted.length - 1}
                    onClick={() => move(index, 1)}
                  >
                    <ArrowDown className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-destructive"
                    onClick={() => requestDelete(status)}
                  >
                    <Trash className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>

            <form
              className="flex items-center gap-2 pt-2 border-t"
              onSubmit={(e) => {
                e.preventDefault();
                if (!newLabel.trim()) return;
                const color = DEFAULT_COLORS[sorted.length % DEFAULT_COLORS.length];
                createMutation.mutate({ label: newLabel.trim(), color });
              }}
            >
              <Input
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder="New status name..."
                className="h-9 text-sm flex-1"
              />
              <Button
                type="submit"
                size="sm"
                disabled={createMutation.isPending || !newLabel.trim()}
              >
                <Plus className="h-3.5 w-3.5 mr-1" />
                Add
              </Button>
            </form>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
